import React from 'react';
import styled from 'styled-components';
import { Icon } from '@tviklax/ui-components';

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${props => props.theme.shadows.md};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const Title = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.gray800};
`;

const FinanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const FinanceBox = styled.div`
  padding: 16px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BoxLabel = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.gray500};
  font-weight: 600;
  text-transform: uppercase;
`;

const BoxValue = styled.span`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => props.theme.colors.gray800};
  font-variant-numeric: tabular-nums;
`;

const PayoutDate = styled.div`
  grid-column: span 2;
  margin-top: 8px;
  padding: 12px;
  background-color: ${props => props.theme.colors.success + '10'};
  border: 1px dashed ${props => props.theme.colors.success};
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: ${props => props.theme.colors.success};
  font-weight: 600;
`;

interface FinanceSummaryProps {
  receivables: string;
  overdue: string;
  nextPayout: string;
  balance: string;
}

export const FinanceSummary: React.FC<FinanceSummaryProps> = ({ receivables, overdue, nextPayout, balance }) => {
  const parseMoney = (value: string) => {
    const parsed = Number(value.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const receivablesValue = parseMoney(receivables);
  const overdueValue = parseMoney(overdue);
  const overduePercent = receivablesValue > 0 ? Math.round((overdueValue / receivablesValue) * 100) : null;

  return (
    <Card>
      <Header>
        <Icon name="CreditCard" color="#3b82f6" size={20} />
        <Title>Finance Snapshot</Title>
      </Header>
      <FinanceGrid>
        <FinanceBox>
          <BoxLabel>Receivables</BoxLabel>
          <BoxValue>{receivables}</BoxValue>
        </FinanceBox>
        <FinanceBox style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
          <BoxLabel>Overdue</BoxLabel>
          <BoxValue style={{ color: '#dc2626' }}>
            {overdue}
            {overduePercent !== null && (
              <span style={{ fontSize: '0.75rem', fontWeight: 600, marginLeft: '4px' }}>
                ({overduePercent}%)
              </span>
            )}
          </BoxValue>
        </FinanceBox>
        <FinanceBox>
          <BoxLabel>Wallet Balance</BoxLabel>
          <BoxValue>{balance}</BoxValue>
        </FinanceBox>
        <FinanceBox>
          <BoxLabel>Pending Payouts</BoxLabel>
          <BoxValue>—</BoxValue>
        </FinanceBox>
        <PayoutDate>
          <Icon name="Calendar" size={14} />
          Next Payout Date: {nextPayout}
        </PayoutDate>
      </FinanceGrid>
      <div style={{ marginTop: '24px', paddingTop: '12px', borderTop: '1px solid #f1f5f9', textAlign: 'right' }}>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
          Calculated as of {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      </div>
    </Card>
  );
};
